import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from "@angular/core";
import { MatButton } from "@angular/material/button";
import { UntilDestroy } from "@ngneat/until-destroy";
import { NotificationsService } from "app/layout/common/notifications/notifications.service";
import { Notification } from "app/layout/common/notifications/notifications.types";
import { MetaData, NgEventBus } from "ng-event-bus";
import { Subject, Subscription, take } from "rxjs";

@UntilDestroy()
@Component({
    selector: "notifications",
    templateUrl: "./notifications.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: "notifications",
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild("notificationsOrigin") private _notificationsOrigin: MatButton;
    @ViewChild("notificationsPanel") private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[] = [];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private subscriptions = new Subscription();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private eventBus: NgEventBus
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        const subscription = this.eventBus.on('notification:created')
            .subscribe((meta: MetaData) => {
                const notification = meta.data as Notification;
                this._appendNotifications([notification]);
                this._calculateUnreadCount();

                 // Mark for check
                this._changeDetectorRef.markForCheck();
            })

        this.subscriptions.add(subscription);

        const biAlertSubscritpion = this.eventBus.on('alert:*')
            .subscribe((meta: MetaData) => {
                switch(meta.key) {
                    case 'alert:markAllRead':
                        this.notifications = [];
                        break;
                    case 'alert:markRead':
                    case 'alert:delete': 
                        const index = this.notifications.findIndex(n => n.id === meta.data as string);
                        if (index !== -1) {
                            this.notifications.splice(index, 1);
                            
                        }
                        break;
                }

                this._calculateUnreadCount();

                // Mark for check
                this._changeDetectorRef.markForCheck();


            })

        this.subscriptions.add(biAlertSubscritpion);
        this.load();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }

        this.subscriptions.unsubscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    load(): void {
        this._notificationsService
            .getAll()
            .pipe(take(1))
            .subscribe(notifications => {
                this.notifications = notifications.sort((a, b) => {
                    const d1 = new Date(a.time);
                    const d2 = new Date(b.time);

                    return d2.getTime() - d1.getTime();
                });

                // Calculate the unread count
                this._calculateUnreadCount();

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * Open the notifications panel
     */
    openPanel(): void {

        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        // Mark all as read
        this._notificationsService.markAllAsRead()
            .pipe(
                take(1),
            )
            .subscribe(() => {
                this.load();
                this._changeDetectorRef.markForCheck();
            });
    }

    markAsRead(notification: Notification): void {
        this._notificationsService.markAsRead(notification.id)
            .pipe(
                take(1),
            )
            .subscribe(() => {
                const index = this.notifications.findIndex(n => n.id === notification.id);
                this.notifications.splice(index, 1);
                this._calculateUnreadCount();
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void {
        // Toggle the read status
        notification.read = !notification.read;

        this._notificationsService.markAsRead(notification.id);

        // Update the notification
        //this._notificationsService.update(notification.id, notification).pipe(take(1)).subscribe(() => this.load());
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void {
        // Delete the notification
        this._notificationsService.delete(notification).pipe(take(1)).subscribe(() => {
            this.load();
            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: "fuse-backdrop-on-mobile",
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: "start",
                        originY: "bottom",
                        overlayX: "start",
                        overlayY: "top",
                    },
                    {
                        originX: "start",
                        originY: "top",
                        overlayX: "start",
                        overlayY: "bottom",
                    },
                    {
                        originX: "end",
                        originY: "bottom",
                        overlayX: "end",
                        overlayY: "top",
                    },
                    {
                        originX: "end",
                        originY: "top",
                        overlayX: "end",
                        overlayY: "bottom",
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        let count = 0;

        if (this.notifications && this.notifications.length) {
            count = this.notifications.filter(notification => notification.read === false).length;
        }

        this.unreadCount = count;
    }

    private _appendNotifications(newNotifications: Notification[]): void {

        if (!this.notifications?.length) {
            this.notifications.push(...newNotifications);

        } else {

            // Load the notifications
            for (const notif of newNotifications) {

                const persistentNotif = this.notifications.find(n => n.id === notif.id);

                if (!persistentNotif) {
                    this.notifications.push(notif);

                } /*else if (notif.read !== persistentNotif.read) {
                    const index = this.notifications.findIndex(n => n.id === notif.id);
                    this.notifications.splice(index, 1);

                    if (!persistentNotif.read) { //Solo se muestran los no leidos
                        this.notifications.push(notif);
                    }
                }*/

            }
        }


    }

    public getNotificationLink(notification: Notification): string {

        /*if (window.location.pathname === '/bi-alert/view') {
            return notification.link.replaceAll('view', 'view2');
        }*/

        return notification.link;

       
    }
}
